import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Logo } from "../components/Logo";
import { ProductGrid } from "../components/ProductCard";
import { PageHead } from "./Shop";
import { OrdersList } from "./Partner";
import { VerifyEmailNotice } from "./AuthEmail";

import { tx, useLang, txf } from "../lib/i18n";
import { PageLoading } from "../components/PageLoading";
const field = "w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-4 py-3 text-sm outline-none focus:border-[var(--mv-teal)] transition-colors";

export function Login() { useLang();
  const { t, setUser, user } = useApp();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [mode, setMode] = useState(sp.get("mode") === "register" ? "register" : "login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const nxt = sp.get("next");
    const ret = (nxt && nxt.startsWith("/") && !nxt.startsWith("//") ? nxt : null) || sessionStorage.getItem("mivo_return");
    sessionStorage.removeItem("mivo_return");
    nav(ret || (user.role === "super_admin" ? "/admin" : ["agent", "seller"].includes(user.role) ? "/partner" : "/account"), { replace: true });
  }, [user, nav]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", mode === "login" ? { email: f.email, password: f.password } : f);
      setUser(data);
      toast.success(tx(txf("Welcome, {0}", data.name)));
      if (mode === "register") toast(tx("We sent a verification link to your email. Verify it to unlock checkout."));
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mv-container flex min-h-[70vh] items-center justify-center py-16" data-testid="login-page">
      <div className="w-full max-w-md rounded-3xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-8">
        <Logo compact />
        <div className="mt-8 grid grid-cols-2 rounded-xl bg-[var(--mv-bg)] p-1">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`auth-tab-${m}`}
              className={`rounded-lg py-2 text-sm transition-colors ${mode === m ? "bg-[var(--mv-card-hover)] text-[var(--mv-teal)]" : "text-[var(--mv-muted)]"}`}>
              {tx(m === "login" ? t("auth.signIn") : t("auth.signUp"))}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-6 space-y-3">
          {tx(mode === "register" && <input required className={field} placeholder={t("auth.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="auth-name-input" />)}
          <input required type="email" className={field} placeholder={t("auth.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="auth-email-input" />
          <input required type="password" minLength={mode === "register" ? 8 : undefined} className={field} placeholder={t("auth.password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="auth-password-input" />
          {tx(err && <p className="text-sm text-red-400" data-testid="auth-error">{tx(err)}</p>)}
          <button disabled={busy} className="mv-btn-primary w-full rounded-xl py-3.5 text-sm disabled:opacity-60" data-testid="auth-submit-button">{tx(busy ? t("common.loading") : mode === "login" ? t("auth.signIn") : t("auth.signUp"))}</button>
        </form>
        {mode === "login" && <Link to="/forgot-password" className="mt-3 block text-right text-xs text-[var(--mv-teal)]" data-testid="forgot-password-link">{tx("Forgot password?")}</Link>}
        {mode === "login" && <><div className="my-5 flex items-center gap-3 text-xs text-[var(--mv-dim)]"><span className="h-px flex-1 bg-[var(--mv-border-2)]" />{t("auth.or")}<span className="h-px flex-1 bg-[var(--mv-border-2)]" /></div>
        <p className="mb-2 text-center text-[11px] text-[var(--mv-dim)]" data-testid="google-existing-note">{tx("Already joined MIVO with Google? Continue below. Google sign-in is processed by our authentication provider, Emergent. New customers: please use Create account.")}</p>
        <button onClick={google} className="mv-btn-outline flex w-full items-center justify-center gap-3 rounded-xl py-3.5 text-sm" data-testid="google-login-button">
          <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
          {t("auth.google")}
        </button></>}
        <p className="mt-4 text-center text-[11px] text-[var(--mv-dim)]" data-testid="apple-login-note">{t("auth.apple")}</p>
      </div>
    </div>
  );
}

export function AuthCallback() { useLang();
  const { setUser } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then((r) => { setUser(r.data); window.history.replaceState(null, "", loc.pathname); nav(loc.pathname, { replace: true }); })
      .catch((e) => { toast.error(tx(errMsg(e) || "Google sign-in failed")); setUser(false); nav("/login", { replace: true }); });
  }, []); // eslint-disable-line
  return <div className="mv-container py-24 text-[var(--mv-muted)]">{tx("Signing you in…")}</div>;
}

export const RequireAuth = ({ children, role }) => { useLang();
  const { user } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => { if (user === false) nav(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true }); }, [user, nav]); // eslint-disable-line
  if (!user) return <div className="mv-container py-24 text-[var(--mv-muted)]"><PageLoading /></div>;
  if (role && user.role !== role) return <div className="mv-container py-24" data-testid="forbidden">{tx("You do not have access to this area.")}</div>;
  return children;
};

export function Account() { useLang();
  const { user, logout, t } = useApp();
  const nav = useNavigate();
  const roleLabel = { buyer: "Buyer", seller: "Seller", agent: "Agent", support: "Support Staff", super_admin: "Super Admin" }[user.role];
  return (
    <div className="mv-container" data-testid="account-page">
      <PageHead eyebrow={txf("{0} DASHBOARD", tx(roleLabel)?.toUpperCase())} title={txf("Hi, {0}", user.name)} />
      <VerifyEmailNotice />
      <div className="grid gap-5 md:grid-cols-3">
        {[["/account/settings", "Settings · Help & Information"], ["/wallet", "My MIVO Card"], ["/cards", "Explore Cards"], ["/wishlist", t("common.wishlist")], ["/track-order", t("nav.track")], ["/wallet/missing-deposit", "Missing deposit help"], ["/messages", "Messages & Support"], ["/account/security", "Change password"], ["/stores", "Stores"], ["/shop", t("nav.shop")]].map(([to, l]) => (
          <Link key={to} to={to} className="rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-6 hover:border-[var(--mv-teal)]">{tx(l)}</Link>
        ))}
        {tx(["agent", "seller"].includes(user.role) && <Link to="/partner" data-testid="account-partner-link" className="rounded-2xl border border-[var(--mv-teal)] bg-[var(--mv-teal-dim)] p-6 text-[var(--mv-teal)]">{tx(user.role === "agent" ? "Agent" : "Seller")} {tx("dashboard · My Stores")}</Link>)}
        {tx(user.role === "super_admin" && <Link to="/admin" data-testid="account-admin-link" className="rounded-2xl border border-[var(--mv-teal)] bg-[var(--mv-teal-dim)] p-6 text-[var(--mv-teal)]">{tx("Super Admin console")}</Link>)}
      </div>
      <p className="mt-6 text-sm text-[var(--mv-muted)]" data-testid="account-email">{user.email} · {tx(roleLabel)}</p>
      <button onClick={async () => { await logout(); nav("/"); }} className="mt-6 mv-btn-outline rounded-xl px-6 py-3 text-sm" data-testid="logout-button">{t("auth.signOut")}</button>
    </div>
  );
}

export function Wishlist() { const lang = useLang();
  const { t, wishIds } = useApp();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data.items)); }, [wishIds.length, lang]);
  return (
    <div className="mv-container" data-testid="wishlist-page">
      <PageHead eyebrow="MIVO" title={t("wishlist.title")} />
      {tx(items && items.length === 0 && <p className="text-[var(--mv-muted)]" data-testid="wishlist-empty">{t("wishlist.empty")}</p>)}
      {tx(items && <ProductGrid items={items} />)}
    </div>
  );
}

export function TrackOrder() { useLang();
  const { t } = useApp();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(tx(errMsg(e)))); }, []);
  return (
    <div className="mv-container" data-testid="track-order-page">
      <PageHead eyebrow="MIVO" title={t("track.title")} />
      {tx(orders && orders.length === 0 && <p className="text-[var(--mv-muted)]" data-testid="track-empty">{t("track.empty")}</p>)}
      {tx(orders && orders.length > 0 && <OrdersList orders={orders} />)}
    </div>
  );
}

export function AccountSecurity() { useLang();
  const { user, checkAuth } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async () => {
    if (f.new_password !== f.confirm) return toast.error(tx("Passwords don't match"));
    try { await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); toast.success(tx("Password changed")); await checkAuth(); const n = new URLSearchParams(window.location.search).get("next"); nav(n && n.startsWith("/") && !n.startsWith("//") ? n : "/account"); } catch (e) { toast.error(tx(errMsg(e))); }
  };
  const inp = "w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] px-4 py-3 text-sm outline-none focus:border-[var(--mv-teal)]";
  if (!user?.must_change_password && user?.role !== "super_admin") return <PasswordRequest inp={inp} />;
  return (
    <div className="mv-container max-w-md py-16" data-testid="account-security-page">
      <PageHead eyebrow={tx("ACCOUNT SECURITY")} title={tx("Change password")} />
      {tx(user?.must_change_password && <p className="mb-5 rounded-lg border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs text-amber-200" data-testid="must-change-password-notice">{tx("Your account uses a temporary password. Choose a new one to continue.")}</p>)}
      <div className="space-y-3">
        <input type="password" className={inp} placeholder={tx("Current password")} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="security-current-password" />
        <input type="password" className={inp} placeholder={tx("New password (8+ characters)")} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="security-new-password" />
        <input type="password" className={inp} placeholder={tx("Confirm new password")} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="security-confirm-password" />
        <button onClick={save} className="mv-btn-primary rounded-xl px-6 py-3 text-sm" data-testid="security-save-button">{tx("Save new password")}</button>
      </div>
    </div>
  );
}

function PasswordRequest({ inp }) { useLang();
  const [cur, setCur] = useState("");
  const [reason, setReason] = useState("");
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/auth/password-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const open = reqs.find((r) => ["pending", "verification_requested", "approved"].includes(r.status));
  const submit = async () => { try { await api.post("/auth/password-requests", { current_password: cur, reason }); setCur(""); toast.success(tx("Request sent — pending Super Admin approval")); load(); } catch (e) { toast.error(tx(errMsg(e))); } };
  const lock = async () => { if (!window.confirm(tx("Sign out of every device and alert MIVO Support?"))) return; await api.post("/auth/security-lock").catch(() => {}); toast.success(tx("All sessions revoked. Please sign in again.")); window.location.href = "/"; };
  return (
    <div className="mv-container max-w-md py-16" data-testid="account-security-page">
      <PageHead eyebrow={tx("ACCOUNT SECURITY")} title={tx("Change password")} />
      <p className="mb-4 text-xs text-[var(--mv-muted)]">{tx("For your protection, password changes are approved by MIVO Super Admin. Your current password keeps working until you choose a new one through the secure single-use link you'll receive after approval. MIVO staff never see your password.")}</p>
      {open ? <p className="rounded-xl border border-amber-300/40 bg-amber-300/5 p-3 text-xs text-amber-200" data-testid="pw-request-status">{txf("Request {0}: {1}", open.ref, tx(open.status === "approved" ? "approved — check your notifications for the secure link" : open.status === "verification_requested" ? "MIVO needs to verify your identity — check Messages" : "pending Admin approval"))}</p>
        : <div className="space-y-3">
          <input type="password" className={inp} placeholder={tx("Current password")} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-request-current" />
          <input className={inp} placeholder={tx("Reason (optional)")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pw-request-reason" />
          <button onClick={submit} className="mv-btn-primary rounded-xl px-6 py-3 text-sm" data-testid="pw-request-submit">{tx("Request password change")}</button>
        </div>}
      <button onClick={lock} className="mt-8 text-xs text-red-300 underline" data-testid="security-lock">{tx("I think my account is compromised — sign out everywhere")}</button>
    </div>
  );
}

export function PasswordGate() { useLang();
  const { user } = useApp();
  const loc = useLocation();
  if (user?.must_change_password && loc.pathname !== "/account/security") return <Navigate to={`/account/security?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return null;
}
